import { useState } from 'react';
import { adviserApi, type PracticeInvitation, type PracticeMember } from '../../advisers/api';

export default function AdviserTeamPanel({
  practiceId,
  members,
  invitations,
  onChanged,
}: {
  practiceId: string;
  members: PracticeMember[];
  invitations: PracticeInvitation[];
  onChanged: () => Promise<void>;
}) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [acceptUrl, setAcceptUrl] = useState('');
  const [removing, setRemoving] = useState<PracticeMember | null>(null);
  return (
    <section>
      <div className="adviser-section-heading">
        <div>
          <h2>Your practice team</h2>
          <p>Invite staff, then assign the companies they work with from the Clients tab.</p>
        </div>
      </div>
      <form
        className="adviser-panel adviser-inline-form"
        onSubmit={async (event) => {
          event.preventDefault();
          const form = event.currentTarget;
          const email = String(new FormData(form).get('email'));
          setBusy(true);
          setError('');
          try {
            const result = await adviserApi.inviteStaff(practiceId, email);
            setAcceptUrl(result.acceptUrl);
            form.reset();
            await onChanged();
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Invitation failed.');
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          Staff email
          <input required name="email" type="email" placeholder="colleague@yourfirm.com" />
        </label>
        <button className="adviser-button" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create invitation'}
        </button>
      </form>
      {error && (
        <p className="adviser-error" role="alert">
          {error}
        </p>
      )}
      {acceptUrl && (
        <div className="adviser-notice">
          <strong>Invitation ready to share</strong>
          <p>
            Share this private link with the invited staff member. They must sign in with the
            invited email.
          </p>
          <input
            aria-label="Staff invitation link"
            value={acceptUrl}
            readOnly
            onFocus={(event) => event.target.select()}
          />
        </div>
      )}
      <div className="adviser-panel">
        <h3>Members</h3>
        {members.map((member) => (
          <div className="adviser-team-row" key={member.userId}>
            <div>
              <strong>{member.name || member.email}</strong>
              <p>{member.email}</p>
            </div>
            <span className="adviser-badge">{member.role}</span>
            {member.role === 'staff' && (
              <button
                type="button"
                className="adviser-button adviser-button-quiet"
                onClick={() => setRemoving(member)}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>
      {invitations.length > 0 && (
        <div className="adviser-panel">
          <h3>Invitations</h3>
          {invitations.map((invitation) => (
            <div className="adviser-team-row" key={invitation.id}>
              <div>
                <strong>{invitation.email}</strong>
                <p>Expires {new Date(invitation.expiresAt).toLocaleDateString()}</p>
              </div>
              <span className="adviser-status">{invitation.status}</span>
              {invitation.status === 'pending' && (
                <button
                  className="adviser-button adviser-button-quiet"
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError('');
                    try {
                      await adviserApi.revokeStaffInvitation(practiceId, invitation.id);
                      await onChanged();
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Unable to revoke invitation.');
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Revoke invitation
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {removing && (
        <div className="adviser-modal-backdrop">
          <section
            className="adviser-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="remove-staff-title"
          >
            <h2 id="remove-staff-title">Remove {removing.name || removing.email}?</h2>
            <p>This removes their practice membership and all delegated client assignments.</p>
            {error && (
              <p className="adviser-error" role="alert">
                {error}
              </p>
            )}
            <div className="adviser-actions">
              <button
                className="adviser-button"
                type="button"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setError('');
                  try {
                    await adviserApi.removeStaff(practiceId, removing.userId);
                    await onChanged();
                    setRemoving(null);
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Unable to remove staff.');
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Remove staff member
              </button>
              <button
                className="adviser-button adviser-button-secondary"
                type="button"
                disabled={busy}
                onClick={() => setRemoving(null)}
              >
                Cancel
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
